import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalHeader,
  Row,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import Axios from "../../lib/Axios.js";
import TempTableForm from "./helper/tempTableForm.jsx";
import TempTableCreateForm from "./helper/tempTableCreateForm.jsx";
import TeempTableResetUserForm from "./helper/tempTableResetUserForm.jsx";

const TempTable = () => {
  const [data, setData] = useState([]);
  const [openTnC, setOpenTnC] = useState(false);
  const [addTnC, setAddTnC] = useState(false);
  const [resetUserTnC, setResetUserTnC] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editData, setEditData] = useState(null);

  // Columns definition
  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Temp Table Id</span>,
        selector: (row) => row.id,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">username</span>,
        selector: (row) => row.username,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Date Of Birth</span>,
        selector: (row) => row.dob,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Email</span>,
        selector: (row) => row.email,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">email OTP</span>,
        selector: (row) => row.emailOTP,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">gender</span>,
        selector: (row) =>
          row.gender == 1 ? "Male" : row.gender == 2 ? "Female" : "Other",
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Country</span>,
        selector: (row) => row.country,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">State</span>,
        selector: (row) => row.state,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">City</span>,
        selector: (row) => row.city,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">resent OTP Count</span>,
        selector: (row) => row.resentOTPCount,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Created at</span>,
        selector: (row) => new Date(row.isCreated).toLocaleString(),
        sortable: true,
      },
      {
        name: <span className=" d-flex font-weight-bold fs-13">Reset OTP</span>,
        cell: (row) => (
          <>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                color="success"
                onClick={() => handleResetOtp(row)} // Separate function for Reset OTP
                className="btn btn-sm btn-success edit-item-btn"
              >
                Reset OTP
              </Button>
            </div>
          </>
        ),
      },
      {
        name: <span className=" d-flex font-weight-bold fs-13">Delete Users</span>,
        cell: (row) => (
          <>
            <div className="d-flex gap-2">
              <Button
                size="sm"
                color="danger" // Different color for Delete User
                onClick={() => handleDeleteUser(row)} // Separate function for Delete User
                className="remove"
              >
                Delete User
              </Button>
            </div>
          </>
        ),
      },
    ],
    []
  );

  const handleResetOtp = async (value) => {
    try {
     const dt={
        tempTableId:value.id
      }
      const result = await Axios.post("/cms/cms-reset/user-temp-table-otp",dt);
      fetchList()
    } catch (error) {
      console.error("Error fetching terms and conditions:", error);
    }
  };

  const handleDeleteUser = async (value) => {
    try {
      const dt={
        tempTableId:value.id
      }
      const result = await Axios.post("/cms/cms-delete/user-temp-table",dt);
      fetchList()
    } catch (error) {
      console.error("Error fetching terms and conditions:", error);
    }
  };

  // Fetch list of terms and conditions
  const fetchList = async () => {
    try {
      const result = await Axios.post("/cms/cms-get/temp-table");
      setData(result?.data?.result?.results || []);
    } catch (error) {
      console.error("Error fetching terms and conditions:", error);
    }
  };

  // Toggle modals
  const toggleOpenTnC = () => setOpenTnC(!openTnC);
  const toggleAddTnC = () => setAddTnC(!addTnC);
  const toggleResetTnC = () => setResetUserTnC(!resetUserTnC);

  // Handle edit action
  const handleEdit = (row) => {
    setEditData(row);
    toggleOpenTnC();
  };

  // Filter data
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return []; // Ensure data is an array before filtering
    }

    return data.filter((item) => {
      const searchLower = searchTerm.toString().toLowerCase();
      return (
        item?.id?.toString().toLowerCase().includes(searchLower) ||
        item?.username?.toLowerCase().includes(searchLower) ||
        item?.dob?.toLowerCase().includes(searchLower) ||
        item?.email?.toLowerCase().includes(searchLower) ||
        item?.emailOTP?.toString().toLowerCase().includes(searchLower) ||
        (item?.gender === 1 ? "male" : item?.gender === 2 ? "female" : "other")
          .toLowerCase()
          .includes(searchLower) ||
        item?.country?.toLowerCase().includes(searchLower) ||
        item?.state?.toLowerCase().includes(searchLower) ||
        item?.city?.toLowerCase().includes(searchLower) ||
        item?.resentOTPCount?.toString().toLowerCase().includes(searchLower) ||
        new Date(item?.isCreated)
          .toLocaleString()
          .toLowerCase()
          .includes(searchLower)
      );
    });
  }, [data, searchTerm]);

  useEffect(() => {
    fetchList();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Temp Table" />
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Temp Table</h4>
              </CardHeader>
              <CardBody>
                <Row className="g-4 mb-3">
                  <Col sm>
                    <Button color="success" onClick={toggleResetTnC}>
                      <i className="mdi mdi-account align-bottom me-1"></i>{" "}
                      Delete Temp User Table
                    </Button>
                  </Col>
                  <Col sm>
                    <div className="d-flex justify-content-sm-end">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </Col>
                </Row>
                <DataTable columns={columns} data={filteredData} pagination />
              </CardBody>
            </Card>
          </Col>
        </Container>
      </div>

      {/* View TnC Modal */}
      <Modal isOpen={openTnC} toggle={toggleOpenTnC} centered>
        <ModalHeader className="bg-light p-3" toggle={toggleOpenTnC}>
          Read Cookie Policy
        </ModalHeader>
        <TempTableForm data={editData} refetch={fetchList} />
      </Modal>

      {/* Create TnC */}
      <Modal isOpen={addTnC} toggle={toggleAddTnC} centered>
        <ModalHeader className="bg-light p-3" toggle={toggleAddTnC}>
          Write Cookie Policy
        </ModalHeader>
        <TempTableCreateForm refetch={fetchList} toggle={toggleAddTnC} />
      </Modal>

      {/* Create TnC */}
      <Modal isOpen={resetUserTnC} toggle={toggleResetTnC} centered>
        <ModalHeader className="bg-light p-3" toggle={toggleResetTnC}>
          Confirm deleting the temp table
        </ModalHeader>
        <TeempTableResetUserForm refetch={fetchList} toggle={toggleResetTnC} />
      </Modal>
    </React.Fragment>
  );
};

export default TempTable;
